import { isEqual } from 'es-toolkit';

import type { JsonBalanceAsset } from 'src/store/shared/balances/types';
import type {
  HistoryDataItem,
  JsonBalanceHistoryAsset,
} from 'src/store/shared/balance-history/types';

import {
  reDLA,
  reFEE,
  reMM,
  reARBITRAGE,
  reADAPTIVE,
  reICEBERG,
  reMENKAR,
  reLIQUIDITY,
  USD_STABLE_TOKENS,
  smallTokensThreshold,
} from './constants';
import type { GetGroupMapParams, HiddenBalanceCounts, PriceAssetParams } from './types';

/**
 * Stable equality check for {@link HiddenBalanceCounts}.
 *
 * Used as Reselect's `resultEqualityCheck` so the `select*HiddenBalanceCounts`
 * selectors keep returning the same reference while the counts are unchanged,
 * preventing unnecessary re-renders of `HiddenFiltersAlert`. Compares structurally,
 * so any field added to {@link HiddenBalanceCounts} is covered automatically.
 */
export const hiddenCountsEqualityCheck = (
  a: HiddenBalanceCounts,
  b: HiddenBalanceCounts,
): boolean => isEqual(a, b);

// Object literal for default no data state
const objectNoData = {
  volume: 0,
  volumeUSD: 0,
  isSmallBalance: false,
};

export const isDla = (label: string) => reDLA.test(label);

export const getToken = (token: string, tokenMap: Record<string, string>): string =>
  tokenMap[token.toUpperCase()] ?? token;

export const isDoublingAsset = (
  asset: JsonBalanceHistoryAsset,
  // balance: JsonBalanceHistoryExtended,
): boolean => {
  return asset.isDoubling ?? false;
};

// helper to get group name from account label
export const getGroupMap = ({ exchange, account, isWallet }: GetGroupMapParams) => {
  const walletsGroup = 'On-Chain Wallets';
  let exchangeGroup = exchange?.name ?? '';
  let accountGroup = '';

  const name = (account?.label ?? '').toUpperCase();

  if (isWallet) {
    exchangeGroup = walletsGroup;
  } else if (reARBITRAGE.test(name) && exchangeGroup === '') {
    exchangeGroup = 'Arbitrage';
  }

  if (isDla(name)) {
    accountGroup = 'DLA';
  } else if (reMM.test(name)) {
    accountGroup = 'MM';
  } else if (reFEE.test(name)) {
    accountGroup = 'Fee';
  } else if (isWallet) {
    accountGroup = walletsGroup;
  } else if (reARBITRAGE.test(name)) {
    accountGroup = 'Arbitrage';
  } else if (reADAPTIVE.test(name) || reICEBERG.test(name)) {
    accountGroup = 'Sell';
  } else if (reMENKAR.test(name)) {
    accountGroup = 'Menkar';
  } else if (reLIQUIDITY.test(name)) {
    accountGroup = 'Liquidity';
  } else {
    accountGroup = 'Other';
  }
  return {
    exchange: exchangeGroup,
    type: accountGroup,
  };
};

/**
 * Whether `token` is a USD-pegged stablecoin from {@link USD_STABLE_TOKENS}, ignoring case and
 * surrounding whitespace (exchanges and wallets report e.g. `USDe` / `USDC.e`).
 */
export const isStableToken = (token: string): boolean =>
  USD_STABLE_TOKENS.has(token.trim().toUpperCase());

// 0 = quote price = lastPrice * quotePrice
// 1 = base price =  lastPrice * quotePrice
// 2 = other price =  lastPrice * quotePrice, except a stablecoin's, which is already in USD
export const getPrice = (assets: PriceAssetParams) => {
  if (isStableToken(assets.token) && assets.type === 2) {
    return assets.price;
  }

  return assets.price * assets.quotePrice;
};

export const getIsSmallBalance = (asset: JsonBalanceAsset) => {
  if (asset.lastPrice === 0) {
    return false;
  }

  const volumeUSD = asset.volume * asset.lastPrice;
  return Math.abs(volumeUSD) < smallTokensThreshold;
};

export const getTargetCurrency = ({
  targetKey,
  token = '',
}: {
  targetKey: string;
  token?: string;
}) => (targetKey === 'volumeUSD' || ['USD', 'USDT', 'USDC'].includes(token) ? 'USD' : token);

export const isNotAssetData = (asset: HistoryDataItem | null) => {
  if (!asset) return true;
  const { volume, volumeUSD, isSmallBalance } = asset;
  return isEqual({ volume, volumeUSD, isSmallBalance }, objectNoData);
};

/**
 * Gets the CSS class name for a balance table row based on whether it's a subtotal
 * and the number of children in its group.
 * @param record - The table row record
 * @param dataSource - The full data source array to look up the parent group
 * @param showSubtotals - Whether subtotals should be shown
 * @param totalCls - CSS class to apply for subtotal rows
 * @returns CSS class name string
 */
export const getBalanceRowClassName = <T extends { key: string | number; children?: T[] }>({
  record,
  dataSource,
  showSubtotals,
  totalCls,
}: {
  record: T;
  dataSource: T[];
  showSubtotals: boolean;
  totalCls: string;
}): string => {
  const recordKey = String(record.key);

  if (!recordKey.startsWith('subtotal:')) {
    return '';
  }

  const groupKey = recordKey.replace('subtotal:', 'group:');
  const group = dataSource.find((item) => String(item.key) === groupKey);
  const groupChildren = group?.children ?? [];
  const groupSize = showSubtotals ? groupChildren.length - 1 : groupChildren.length; // exclude subtotal row

  return showSubtotals && groupSize > 1 ? totalCls : 'hidden';
};
